import React, { ReactNode } from 'react';
import Link from 'next/link';
import { cn } from '../cn';
import {
  buttonVariants,
  buttonShineClasses,
  buttonDisabledClasses,
} from '../recipes/button';

type ButtonVariant =
  | 'primary'
  | 'upgrade'
  | 'danger'
  | 'outline'
  | 'ghost';

type ButtonTags = {
  a: React.AnchorHTMLAttributes<HTMLAnchorElement>;
  button: React.ButtonHTMLAttributes<HTMLButtonElement>;
};

function ShineBorder() {
  return (
    <div className="btn-border-mask">
      <div className="btn-border-glow" />
    </div>
  );
}

function resolveVariant(
  variant: ButtonVariant | undefined,
  outline: boolean
): ButtonVariant {
  if (variant) return variant;
  return outline ? 'outline' : 'primary';
}

export function Button<T extends keyof ButtonTags>({
  tag: Tag,
  outline = false,
  variant,
  size = 'md',
  className,
  children,
  ...attrs
}: {
  tag: T | typeof Link;
  outline?: boolean;
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  className?: string;
  disabled?: boolean;
  children?: ReactNode;
} & ButtonTags[T]) {
  const resolved = resolveVariant(variant, outline);
  const showShine =
    (resolved === 'primary' || resolved === 'upgrade') && !attrs.disabled;

  const classes = cn(
    buttonVariants({ variant: resolved, size }),
    showShine && buttonShineClasses,
    attrs.disabled && buttonDisabledClasses,
    className
  );

  const content = showShine ? (
    <>
      <ShineBorder />
      {children}
    </>
  ) : (
    children
  );

  return (
    <Tag {...(attrs as any)} className={classes}>
      {content}
    </Tag>
  );
}

export function ButtonLink({
  outline = false,
  variant,
  size = 'md',
  className,
  children,
  target = '_self',
  onClick,
  ...attrs
}: {
  outline?: boolean;
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  className?: string;
  children: ReactNode;
  href?: string;
  id?: string;
  disabled?: boolean;
  target?: string;
  rel?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const resolved = resolveVariant(variant, outline);
  const showShine =
    (resolved === 'primary' || resolved === 'upgrade') && !attrs.disabled;

  const classes = cn(
    buttonVariants({ variant: resolved, size }),
    showShine && buttonShineClasses,
    attrs.disabled && buttonDisabledClasses,
    className
  );

  return (
    <Link
      {...(attrs as any)}
      onClick={onClick}
      target={target}
      className={classes}
    >
      {showShine && <ShineBorder />}
      {children}
    </Link>
  );
}
